import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const PlanMetadataViewSchema = z.object({
  planName: z.string(),
  isActive: z.boolean(),
  displayOrder: z.number().nullable(),
  localizedNames: z.record(z.string(), z.string()),
  localizedDescriptions: z.record(z.string(), z.string()),
  localizedImageUrls: z.record(z.string(), z.string()),
  marketingTagline: z.string().nullable(),
  iconRef: z.string().nullable(),
  color: z.string().nullable(),
  category: z.string().nullable(),
  previewImageUrl: z.string().nullable(),
});

const UpdatePlanMetadataSchema = z.object({
  localizedNames: z.record(z.string(), z.string()).optional(),
  localizedDescriptions: z.record(z.string(), z.string()).optional(),
  localizedImageUrls: z.record(z.string(), z.string()).optional(),
  marketingTagline: z.string().nullable().optional(),
  iconRef: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  previewImageUrl: z.string().nullable().optional(),
});

const AddonMetadataViewSchema = PlanMetadataViewSchema.omit({ planName: true }).extend({
  addOnName: z.string(),
  stackable: z.boolean(),
});

const UpdateAddonMetadataSchema = UpdatePlanMetadataSchema.extend({
  stackable: z.boolean().optional(),
});

const ReorderInputSchema = z.object({
  orderedNames: z.array(z.string()),
});

const LocalizedListingItemSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  marketingTagline: z.string().nullable(),
  category: z.string().nullable(),
  iconRef: z.string().nullable(),
  color: z.string().nullable(),
  price: z.number().nullable(),
  currency: z.string(),
  billingPeriod: z.string(),
  trial: z
    .object({
      durationDays: z.number(),
      fixedPrice: z.number(),
    })
    .nullable(),
  features: z.record(z.string(), z.union([z.boolean(), z.number(), z.string()])),
  addOns: z.array(
    z.object({
      addOnName: z.string(),
      displayName: z.string(),
      description: z.string(),
      imageUrl: z.string().nullable(),
      price: z.number().nullable(),
      billingPeriod: z.string(),
      trial: z.object({ durationDays: z.number(), fixedPrice: z.number() }).nullable(),
      features: z.record(z.string(), z.union([z.boolean(), z.number(), z.string()])),
      stackable: z.boolean(),
    })
  ),
});

const errorSchema = z.object({ message: z.string() });

export const planMetadataContract = c.router({
  getPlanMetadata: {
    method: "GET",
    path: "/billing/metadata/plans/:planName",
    pathParams: z.object({ planName: z.string() }),
    responses: {
      200: PlanMetadataViewSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get display metadata for a plan",
  },

  updatePlanMetadata: {
    method: "PUT",
    path: "/billing/metadata/plans/:planName",
    pathParams: z.object({ planName: z.string() }),
    body: UpdatePlanMetadataSchema,
    responses: {
      200: PlanMetadataViewSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update display metadata for a plan",
  },

  getAddonMetadata: {
    method: "GET",
    path: "/billing/metadata/addons/:addOnName",
    pathParams: z.object({ addOnName: z.string() }),
    responses: {
      200: AddonMetadataViewSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get display metadata for an add-on",
  },

  listAddonMetadata: {
    method: "GET",
    path: "/billing/metadata/addons",
    responses: {
      200: z.array(AddonMetadataViewSchema),
      401: errorSchema,
      500: errorSchema,
    },
    summary: 'List display metadata for all add-ons (used to derive flags like "stackable")',
  },

  updateAddonMetadata: {
    method: "PUT",
    path: "/billing/metadata/addons/:addOnName",
    pathParams: z.object({ addOnName: z.string() }),
    body: UpdateAddonMetadataSchema,
    responses: {
      200: AddonMetadataViewSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update display metadata for an add-on",
  },

  reorderPlans: {
    method: "PUT",
    path: "/billing/metadata/plans/order",
    body: ReorderInputSchema,
    responses: {
      200: z.object({ message: z.string() }),
      400: errorSchema,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Reorder plans in the catalog listing",
  },

  reorderAddOns: {
    method: "PUT",
    path: "/billing/metadata/addons/order",
    body: ReorderInputSchema,
    responses: {
      200: z.object({ message: z.string() }),
      400: errorSchema,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Reorder add-ons in the catalog listing",
  },

  getLocalizedListing: {
    method: "GET",
    path: "/billing/catalog/listing",
    query: z.object({
      locale: z.string().default("en"),
      currency: z.string().default("EUR"),
    }),
    responses: {
      200: z.array(LocalizedListingItemSchema),
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Get localized catalog listing with prices, metadata, and feature mapping",
  },
});
